/* eslint-disable react-hooks/exhaustive-deps */
import { useRef } from 'react';
import { Box, Center, Flex, IconButton, Text, Tooltip } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { DownloadIcon } from '@chakra-ui/icons';
import { chartBackgroundColor } from '../../Miscellaneous/plugins-chart';
import Chart from 'react-chartjs-2';
import Card from '../../../Card/Card';
import DataLoading from '../../../../components/DataLoading';
import chartDataLabels from 'chartjs-plugin-datalabels';

const chartDataOutsideLabels = {
	id: 'chartDataOutsideLabels',
	afterDraw(chart) {
		const {
			ctx,
			chartArea: { width, height },
		} = chart;
		const cx = chart._metasets[0].data[0].x;
		const cy = chart._metasets[0].data[0].y;
		chart.data.datasets.forEach((dataset, i) => {
			chart.getDatasetMeta(i).data.forEach((datapoint, index) => {
				const { x: a, y: b } = datapoint.tooltipPosition();
				const x = 2 * a - cx;
				const y = 2 * b - cy;
				const halfWidth = width / 2;
				const halfHeight = height / 2;
				const xLine = x > halfWidth ? x + 32 : x - 32;
				const yLine = y > halfHeight ? y : y;
				const space = x > halfWidth ? 2 : -2;
				ctx.setLineDash([1, 1]);
				ctx.beginPath();
				ctx.moveTo(x, y);
				ctx.lineTo(xLine, yLine);
				ctx.strokeStyle = dataset?.backgroundColor[index];
				ctx.stroke();
				ctx.font = '10px Arial';
				const textXPosition = x >= halfWidth ? 'left' : 'right';
				ctx.textAlign = textXPosition;
				ctx.textBaseline = 'middle';
				ctx.setLineDash([0, 0]);
				ctx.fillStyle = dataset.backgroundColor[index];
				ctx.fillText(chart.data.labels[index], xLine + space, yLine - 6);
				ctx.fillText(dataset.data[index] + '%', xLine + space, yLine + 6);
			});
		});
	},
};

function Graph({ loading = false, title = null, dataset = null, fileName = null }) {
	const chartRef = useRef(null);
	const { t } = useTranslation();

	return (
		<Box w={['0%', '50%', '100%']}>
			<Card>
				<Flex justifyContent="center" alignItems="center" flexDirection="column" p={4}>
					<Text fontSize="lg" fontWeight="bold" color="gray.600" noOfLines={1} pl={4} pr={4}>
						{t(title)}
					</Text>
				</Flex>
				<DataLoading loading={loading} loadedData={dataset}>
					{({ loadedData }) => (
						<>
							<Box width="100%" p={4} pt={0}>
								<Center>
									<Chart
										ref={chartRef}
										type="pie"
										width="236px"
										height="236px"
										plugins={[chartDataLabels, chartDataOutsideLabels, chartBackgroundColor]}
										options={{
											maintainAspectRatio: false,
											canvasBackgroundColor: {
												color: '#FFFFFF',
											},
											layout: {
												padding: {
													top: 32,
													right: 16,
													bottom: 16,
													left: 4,
												},
											},
											plugins: {
												title: {
													display: false,
												},
												legend: {
													display: false,
													position: 'bottom',
												},
												datalabels: {
													display: true,
													color: 'white',
													font: {
														family: 'Rajdhani, Hanuman, sans-serif, Moul',
														weight: 'bold',
														size: 10,
													},
													formatter: function (value, context) {
														return value + '%';
													},
												},
												tooltip: {
													callbacks: {
														label: (toolTipItem) => {
															return t(toolTipItem.label) + ': ' + toolTipItem.formattedValue + '%';
														},
													},
												},
											},
										}}
										data={loadedData}
									/>
								</Center>
							</Box>
							<Flex className="hidden-print" justifyContent="flex-end" alignItems="center" p={4}>
								<Tooltip label={t('Download Image')}>
									<IconButton
										size="sm"
										icon={<DownloadIcon w={3} h={3} />}
										onClick={() => {
											const link = document.createElement('a');
											link.download = fileName;
											link.href = chartRef?.current?.toBase64Image('image/jpeg', 1);
											link.click();
										}}
									/>
								</Tooltip>
							</Flex>
						</>
					)}
				</DataLoading>
			</Card>
		</Box>
	);
}

export default Graph;
